import Vue from 'vue';
import axios from 'axios';
import route from '@H/route';
import router from '@R';
import _user from '@H/user';
import { getErrorFromResponse } from '@H/error-codes';

import {
    verify as sharedVerify,
    getUser as sharedGetUser
} from '../../shared';

export default {
    /**
     * Calls Laravel login route
     * @param {Object} ctx - Context set by vuex (store)
     * @param {Object} e - Form event
     */
    async login(ctx, e) {
        let { $gettext } = this._vm;
        let res;
        try {
            ctx.commit('setFormSubmitting', true, { root: true });
            res = await axios.post(route('login'), {
                email: ctx.state.email,
                password: ctx.state.password,
                remember_me: ctx.state.remember_me,
                language: this._vm.$language.current.substring(0, 2),
                callback_url: ctx.state.callbackUrl
            });

            ctx.commit('setFormSubmitting', false, { root: true });

            if (
                typeof res.data.csrf_token == 'undefined'
            ) {
                return ctx.dispatch('Flash/setFlash', res.data.message, {
                    root: true
                });
            }

            ctx.dispatch('updateCsrfToken', res.data.csrf_token);

            const { data: { user: { profile } } } = res;

            if (profile.is_marketing_journey) {
                sessionStorage.setItem('isMarketing', true);
            }

            if (res.data.mustEnrol) {
                if (typeof res.data.addon['price'] != 'undefined' && typeof res.data.addon['currency'] != 'undefined') {
                    commit('setAddon', { price: res.data.addon['price'], currency: res.data.addon['currency'] })
                } else if (res.data.addon['price'] != '' && res.data.addon['currency'] != '') {
                    commit('setAddon', { price: res.data.addon['price'], currency: res.data.addon['currency'] })
                } else {
                    commit('setAddon', { price: '', currency: '' })
                }

                return router.push({
                    name: profile.is_marketing_journey ? 'Marketing Step 1' : 'begin-enrolment'
                });
            }

            if (res.data.force_reset_password) {

                let requests = Object.assign({
                    user: res.data.user
                });

                axios.post(route('password.generate-token'), requests)
                    .then(res => {
                        console.log(res);
                        return window.location.href = '/password/set/' + res.data.token + '?email=' + res.data.email;
                    })
                    .catch(err => {
                        return ctx.dispatch(
                            'Flash/setFlash',
                            {
                                message: $gettext(
                                    err
                                )
                            },
                            { root: true }
                        );
                    });
            }

            ctx.dispatch('Flash/clearFlash', null, { root: true });

            ctx.dispatch('authenticated', {
                token: res.data.token,
                user: res.data.user
            });

        } catch (err) {
            ctx.dispatch('updateCsrfToken', err.response.data.csrf_token);
            if (
                typeof err.response.data.salesforce_account != 'undefined' &&
                err.response.data.salesforce_account !== null
            ) {
                // User provided invalid credentials for our system, but the email exists in salesforce
                // So send them to the register form and pre-populate the basic info
                return _user.setupRegister(
                    err.response.data.salesforce_account
                );
            }

            let defaultMessage = $gettext(
                'We were unable to log you in at this moment. Please try again later.'
            ),
                message = getErrorFromResponse(err, defaultMessage);

            if (
                typeof err.response.data.verified != 'undefined' &&
                err.response.data.verified == false
            ) {
                return router.push({
                    name: 'verify-account',
                    params: {
                        email: ctx.state.email
                    }
                });
            }

            ctx.commit('setFormSubmitting', false, { root: true });
            ctx.commit('password', '');
            ctx.dispatch('Flash/setFlash', { message }, { root: true });
        }
    },

    /**
     * Logs the user out & clears all session values
     * @param ctx - Context set by vuex (store)
     */
    async logout(ctx) {
        let res;
        let params = {};

        try {
            res = await axios.post(route('logout'));
            sessionStorage.removeItem('isMarketing');
            ctx.dispatch('updateCsrfToken', res.data.csrf_token);

            // Wipe out the user from the state so subsequent getUser calls don't reference a cached user
            ctx.commit('Enrolment/setUser', null, {
                root: true
            });
            ctx.commit('hasUserNow', false);
        } catch (err) {
            if (typeof err.response == 'undefined') {
                return ctx.dispatch(
                    'Flash/setFlash',
                    {
                        message: $gettext(
                            'Something went wrong, please try again.'
                        )
                    },
                    { root: true }
                );
            }
        }

        // Keep a hold of the flash message before we perform the redirect to the login screen
        if (ctx.rootState.Flash.flash != '') {
            params.flash = ctx.rootState.Flash.flash;
        }

        return router.push({ name: 'login', params });
    },

    /**
     * Calls Laravel register route
     * @param {Object} ctx - Context set by vuex (store)
     */
    register(ctx) {
        let { $gettext } = this._vm;
        let {
            first_name,
            last_name,
            email,
            password,
            password_confirmation,
            language,
            accepted_terms,
            date_of_birth,
            passwordValidated,
            passwordConfirmed,
            affiliate,
            salutation
        } = ctx.state;
        let res;

        if (!passwordValidated && !passwordConfirmed) {
            ctx.commit('passwordSubmitFailed', true);
            ctx.commit('setFormSubmitting', false, { root: true });
            return ctx.dispatch(
                'Flash/setFlash',
                { message: $gettext('Password validation failed') },
                {
                    root: true
                }
            );
        } else {
            ctx.commit('passwordSubmitFailed', false);
        }

        return new Promise(async (resolve, reject) => {
            try {
                ctx.commit('setFormSubmitting', true, { root: true });
                res = await axios.post(route('register'), {
                    salutation,
                    first_name,
                    last_name,
                    email,
                    password,
                    password_confirmation,
                    language,
                    accepted_terms,
                    date_of_birth,
                    affiliate
                });

                ctx.dispatch('updateCsrfToken', res.data.csrf_token);

                ctx.commit('email', email);
                ctx.commit('setFormSubmitting', false, { root: true });

                router.push({ name: 'verify-account' });
                resolve(true);
            } catch (err) {
                let defaultMessage = $gettext(
                    'There was a problem registering your account, please try again shortly.'
                ),
                    message = getErrorFromResponse(err, defaultMessage);

                ctx.commit('setFormSubmitting', false, { root: true });
                ctx.commit('password_confirmation', '');

                reject(message);
            }
        });
    },

    /**
     * Handles what happens post-successful authentication
     * Takes the user off to FXBlue with the correct authentication querystring
     * @param  ctx
     * @param  data - should contain the user and token
     */
    authenticated(ctx, data) {
        let query = querystring.stringify({
            token: data.token,
            user_id: data.user.id
        });

        let url = ctx.rootState.Auth.callbackUrl;
        let hash = '';
        // If we have a hash on the URL - strip it off so we can add it back on after the token
        if (url.indexOf("#") != -1) {
            let splitByHash = url.split('#');
            url = splitByHash[0];
            hash = '#' + splitByHash[1];
        }

        url += '?' + query;

        // Go to #welcome on first smartcharts log in
        if (ctx.state.go_to_smartcharts && data.user.first_log_in == 'Yes') {
            url += '#welcome';
        } else {
            url += hash;
        }

        window.location.href = url;
    },

    /**
     * Checks if a user is logged in server-side
     * @param  ctx
     */
    async getUser(ctx) {
        return await sharedGetUser('Enrolment', ctx);
    },

    /**
     * Sets the user in state if the user is logged in server-side
     * @param  ctx
     */
    setUser(ctx, user) {
        ctx.commit('email', user.email);
    },

    /**
     * Handles a forgotten password request
     * @param {Object} ctx - Context set by vuex (store)
     * @param {Object} e - Form event
     */
    async forgotPassword(ctx, e) {
        let { $gettext } = this._vm;
        try {
            ctx.commit('setFormSubmitting', true, { root: true });
            await axios.post(route('password.email'), {
                email: ctx.state.email
            });
            ctx.commit('setFormSubmitting', false, { root: true });

            // Redirect to the success screen
            router.push({
                name: 'reset-email-sent',
                params: {
                    email: ctx.state.email
                }
            });
        } catch (err) {
            let defaultMessage = $gettext(
                'There was a problem processing your request, please check your e-mail address and try again.'
            );
            ctx.dispatch(
                'Flash/setFlash',
                { message: getErrorFromResponse(err, defaultMessage) },
                { root: true }
            );

            ctx.commit('setFormSubmitting', false, { root: true });
            ctx.commit('email', '');
        }
    },

    /**
     * Calls Laravel email verification route
     * @param {Object} ctx - Context set by vuex (store)
     */
    async sendVerificationEmail(ctx) {
        // Resent verification e-mail.
        let { $gettext } = this._vm;
        let res;

        ctx.dispatch('Flash/clearFlash', null, { root: true });

        try {
            res = await axios.post(route('verification.resend'), {
                email: ctx.state.email
            });

            // TODO: this may want to go to another screen? Similar to password reset email success screen
            return ctx.dispatch(
                'Flash/setFlash',
                {
                    message: $gettext('Verifiation E-mail sent!'),
                    key: 'success'
                },
                { root: true }
            );
        } catch (err) {
            return ctx.dispatch(
                'Flash/setFlash',
                { message: getErrorFromResponse(err) },
                { root: true }
            );
        }
    },

    /**
     * Resends password reset email
     * @param {Object} ctx - Context set by vuex (store)
     */
    resendEmail(ctx) {
        let { $gettext } = this._vm;
        ctx.commit('setFormSubmitting', true, { root: true });

        ctx.dispatch('Flash/clearFlash', null, { root: true });

        axios
            .post(route('password.email'), {
                email: ctx.state.email
            })
            .then(res => {
                ctx.commit('setFormSubmitting', false, { root: true });
                // Show a flash "email re-sent.."
                ctx.dispatch(
                    'Flash/setFlash',
                    {
                        message: $gettext(
                            'Password Reset Email Sent Successfully'
                        ),
                        key: 'success'
                    },
                    {
                        root: true
                    }
                );
            })
            .catch(err => {
                ctx.commit('setFormSubmitting', false, { root: true });
                ctx.dispatch(
                    'Flash/setFlash',
                    { message: getErrorFromResponse(err) },
                    {
                        root: true
                    }
                );
            });
    },

    /**
     * Verifies that a user has verified their email via the email link
     * @param {Object} ctx - Context set by vuex (store)
     */
    async emailVerify(ctx) {
        const { $gettext } = this._vm;
        await sharedVerify('verification', $gettext, ctx);
    },

    /**
     * Handles password reset
     * @param {Object} ctx - Context set by vuex (store)
     * @param {Object} e - Form event
     */
    async passwordReset(ctx, e) {
        try {
            ctx.commit('setFormSubmitting', true, { root: true });
            await axios
                .post(route('password.reset'), {
                    token: ctx.rootState.route.params.token,
                    password: ctx.state.password,
                    password_confirmation: ctx.state.password_confirmation,
                    email: decodeURIComponent(ctx.rootState.route.query.email)
                });

            // Redirect to callback url
            let redirectUrl = ctx.rootState.route.query.url;
            if (redirectUrl) {
                window.location = decodeURIComponent(redirectUrl);
            }

            // Redirect to the success screen
            ctx.commit('setFormSubmitting', false, { root: true });
            return router.push({
                name: 'password-reset-success'
            });
        } catch (err) {
            throw getErrorFromResponse(err);
        } finally {
            ctx.commit('setFormSubmitting', false, { root: true });
            ctx.commit('password', '');
            ctx.commit('password_confirmation', '');
        }
    },

    /**
     * Verifies that a user has verified their email via the email link
     * @param {Object} ctx - Context set by vuex (store)
     */
     async emailVerify(ctx) {
        const { $gettext } = this._vm;
        await sharedVerify('verification', $gettext, ctx);
    },

    /**
     * Handles set password
     * @param {Object} ctx - Context set by vuex (store)
     * @param {Object} e - Form event
     */
    async passwordSet(ctx, e) {

        try {
            ctx.commit('setFormSubmitting', true, { root: true });

            const res = await axios
                    .post(route('password.set'), {
                        password: ctx.state.password,
                        email: ctx.state.email,
                        user:  ctx.rootState.Enrolment.user
                    });

            if(res.data.code == 'set_password_error') {
                return ctx.dispatch(
                        'Flash/setFlash',
                        {
                            message: $gettext(
                                res.data.message
                            )
                        },
                        {
                            root: true
                        }
                    );
            } else{
                console.log('success')
                window.localStorage.removeItem('sfData');
                window.location.href = res.data.callback_url;
            }

        } catch (err) {
            throw getErrorFromResponse(err);
        } finally {
            ctx.commit('setFormSubmitting', false, { root: true });
            ctx.commit('password', '');
            ctx.commit('password_confirmation', '');
        }
    },

    /**
     * Compares current password to stored password
     * @param {Object} ctx - Context set by vuex (store)
     */
    comparePasswords(ctx) {
        let { password, email } = ctx.state;
        const headers = {
            'X-CSRF-TOKEN'    : window.axios.defaults.headers.common['X-CSRF-TOKEN'],
            'X-Requested-With': 'XMLHttpRequest',
        };
        console.log(headers);
        return new Promise((resolve, reject) => {
            axios
                .post(route('password.check'), { password, email }, headers)
                .then(res => {
                    // res.data.match will be true if the password they've entered
                    // is the same as their existing password
                    if (res.data.match) {
                        ctx.commit('passwordSubmitFailed', true);
                    } else {
                        ctx.commit('passwordSubmitFailed', false);
                    }
                    resolve(res.data.match);
                })
                .catch(err => {
                    let message = getErrorFromResponse(err);

                    ctx.commit('passwordSubmitFailed', true);
                    ctx.dispatch(
                        'Flash/setFlash',
                        { message },
                        {
                            root: true
                        }
                    );

                    reject(message);
                });
        });
    },

    /**
     * Gets allowed salutations for registration
     * @param {Object} ctx - Context set by vuex (store)
     */
    async querySalutations(ctx) {
        try {
            let {
                data: { salutations }
            } = await axios.get('/list/salutations');
            ctx.commit('setSalutations', salutations);
        } catch (err) {
            console.log(err);
        }
    },

    /**
     * Sets affiliate on registration
     * @param {Object} ctx - Context set by vuex (store)
     */
    setAffiliate(ctx, payload) {
        ctx.commit('setAffiliate', payload);
    },

    updateCsrfToken(ctx, token) {
        // refresh the csrf token for subsequent api calls
        window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token;
    },

    /**
     * Compares current password to stored password
     * @param {Object} ctx - Context set by vuex (store)
     * @param {Boolean} payload - Password validated or not
     */
    passwordValidated(ctx, payload) {
        ctx.commit('passwordValidated', payload);
    },

    /**
     * Commits the passwordConcifrmed payload
     * @param {Object} ctx - Context set by vuex (store)
     * @param {Boolean} payload - Password confirmed or not
     */
    setPasswordConfirmed(ctx, payload) {
        ctx.commit('passwordConfirmed', payload);
    },

    /**
     * Handles remember me from FE
     * @param {Object} ctx - Context set by vuex (store)
     */
    rememberMe(ctx) {
        // DEVTODO: Do your thing Joe
        console.log(ctx.state.remember_me);
    },

    /**
     * Handles accepting terms
     * @param {Object} ctx - Context set by vuex (store)
     */
    acceptedTerms(ctx) {
        // DEVTODO: Do your thing Joe
        console.log(ctx.state.accepted_terms);
    },

    /**
     * Handles over 18
     * @param {Object} ctx - Context set by vuex (store)
     */
    over18(ctx) {
        // DEVTODO: Do your thing Joe
        console.log(ctx.state.over_18);
    }
};



// WEBPACK FOOTER //
// ./resources/assets/js/vuex/modules/Auth/actions.js